import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Space, Layout } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useOutletContext } from 'react-router-dom';
import { settingsService, CompanyProfile } from '../../services/settings';

const { Text } = Typography;
const { TextArea } = Input;
const { Content } = Layout;
type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

export const CompanyProfilePage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyProfile | null>(null);
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Company Profile
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getCompanyProfile();
      setCompanyData(data);
      form.setFieldsValue(data);
    } catch (error) {
      message.error('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: CompanyProfile) => {
    try {
      setLoading(true);
      await settingsService.updateCompanyProfile(values);
      message.success('Company profile updated successfully');
      setCompanyData(values);
    } catch (error) {
      message.error('Failed to update company profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Content style={{ padding: '24px' }}>
      <Text type="secondary">Manage your company information and branding</Text>
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        style={{ marginTop: '24px' }}
      >
        <Card title="Basic Information" style={{ marginBottom: '24px' }}>
          <Form.Item
            name="company_name"
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input placeholder="Enter company name" />
          </Form.Item>
          
          <Form.Item
            name="company_description"
            label="Company Description"
          >
            <TextArea rows={3} placeholder="Enter company description" />
          </Form.Item>
          
          <Form.Item
            name="company_logo"
            label="Company Logo URL"
          >
            <Input placeholder="Enter logo URL" />
          </Form.Item>
        </Card>

        <Card title="Contact Information" style={{ marginBottom: '24px' }}>
          <Form.Item
            name="company_address"
            label="Address"
          >
            <TextArea rows={2} placeholder="Enter company address" />
          </Form.Item>
          
          <Form.Item
            name="company_phone"
            label="Phone"
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>
          
          <Form.Item
            name="company_email"
            label="Email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
          
          <Form.Item
            name="company_website"
            label="Website"
          >
            <Input placeholder="Enter website URL" />
          </Form.Item>
        </Card>

        <Card title="Business Information" style={{ marginBottom: '24px' }}>
          <Form.Item
            name="company_tax_id"
            label="Tax ID / Business Registration Number"
          >
            <Input placeholder="Enter tax ID or business registration number" />
          </Form.Item>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
            Save Company Profile
          </Button>
          <Button onClick={() => form.resetFields()}>
            Reset
          </Button>
        </Space>
      </Form>
    </Content>
  );
};
